import { useEffect, useState } from "react";
import { useSubdomain } from "./SubdomainProvider.js";
import AdminLogin, { adminLoginPayload } from "./AdminLogin.js";
import AdminNavigation from "./AdminNavigation.js";
import {
  actorDisplayName,
  fetchActors,
  resolveActorImage,
} from "../config/actorAssets.js";

const API_URL = process.env.REACT_APP_API_URL;

const emptyForm = {
  firstName: "",
  lastName: "",
  headshotUrl: "",
  polaroidUrl: "",
  bio: "",
};

function ActorsAdmin() {
  const [token, setToken] = useState(localStorage.getItem("adminToken") || "");
  const [passwordInput, setPasswordInput] = useState("");
  const [actors, setActors] = useState([]);
  const [error, setError] = useState("");
  const [formError, setFormError] = useState("");
  const [form, setForm] = useState(emptyForm);
  const [editingId, setEditingId] = useState(null);
  const [saving, setSaving] = useState(false);
  const [previewImage, setPreviewImage] = useState(null);
  const subdomain = useSubdomain();

  const isLoggedIn = !!token;

  const loadActors = () =>
    fetchActors(API_URL, subdomain)
      .then(setActors)
      .catch(() => setError("Failed to load actors"));

  const closePreview = () => setPreviewImage(null);

  useEffect(() => {
    if (previewImage) {
      document.body.classList.add("no-scroll");
    } else {
      document.body.classList.remove("no-scroll");
    }
    return () => document.body.classList.remove("no-scroll");
  }, [previewImage]);

  const handleLogin = () => {
    fetch(`${API_URL}/api/login`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: adminLoginPayload(passwordInput, subdomain),
    })
      .then((res) => {
        if (!res.ok) throw new Error("Login failed");
        return res.json();
      })
      .then((data) => {
        localStorage.setItem("adminToken", data.token);
        setToken(data.token);
        setError("");
      })
      .catch(() => {
        setError("Incorrect password");
      });
  };

  useEffect(() => {
    if (!isLoggedIn) return;

    fetchActors(API_URL, subdomain)
      .then(setActors)
      .catch(() => setError("Failed to load actors"));
  }, [isLoggedIn, subdomain]);

  const resetForm = () => {
    setForm(emptyForm);
    setEditingId(null);
    setFormError("");
  };

  const startEdit = (actor) => {
    setEditingId(actor.id);
    setForm({
      firstName: actor.firstName || "",
      lastName: actor.lastName || "",
      headshotUrl: actor.headshotUrl || "",
      polaroidUrl: actor.polaroidUrl || "",
      bio: actor.bio || "",
    });
    setFormError("");
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const usesLocalAssets = (actor) =>
    actor && !actor.headshotUrl && !actor.polaroidUrl;

  const handleSubmit = (e) => {
    e.preventDefault();
    setFormError("");
    setSaving(true);

    const payload = {
      firstName: form.firstName.trim(),
      lastName: form.lastName.trim(),
      headshotUrl: form.headshotUrl.trim(),
      polaroidUrl: form.polaroidUrl.trim(),
      bio: form.bio,
      subdomain: subdomain === "seattle" ? "seattle" : "app",
    };

    const editingActor = actors.find((a) => a.id === editingId);
    const localAssetActor = editingId && usesLocalAssets(editingActor);

    if (!payload.firstName) {
      setFormError("First name is required");
      setSaving(false);
      return;
    }

    if (!localAssetActor) {
      if (!payload.headshotUrl || !payload.polaroidUrl) {
        setFormError("Headshot URL and Polaroid URL are required");
        setSaving(false);
        return;
      }
    }

    const url = editingId
      ? `${API_URL}/api/actors/${editingId}`
      : `${API_URL}/api/actors`;
    const method = editingId ? "PUT" : "POST";

    fetch(url, {
      method,
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify(payload),
    })
      .then(async (res) => {
        const data = await res.json().catch(() => ({}));
        if (!res.ok) throw new Error(data.error || "Save failed");
        return data;
      })
      .then(() => {
        resetForm();
        return loadActors();
      })
      .catch((err) => setFormError(err.message))
      .finally(() => setSaving(false));
  };

  const handleDelete = (actor) => {
    const label = actorDisplayName(actor);
    if (!window.confirm(`Delete actor "${label}"? This cannot be undone.`)) {
      return;
    }

    fetch(`${API_URL}/api/actors/${actor.id}`, {
      method: "DELETE",
      headers: { Authorization: `Bearer ${token}` },
    })
      .then(async (res) => {
        const data = await res.json().catch(() => ({}));
        if (!res.ok) throw new Error(data.error || "Delete failed");
      })
      .then(() => {
        if (editingId === actor.id) resetForm();
        return loadActors();
      })
      .catch((err) => alert(err.message));
  };

  if (!isLoggedIn) {
    return (
      <AdminLogin
        pageTitle="Actors"
        passwordInput={passwordInput}
        setPasswordInput={setPasswordInput}
        onLogin={handleLogin}
        error={error}
      />
    );
  }

  const fieldStyle = {
    display: "block",
    width: "80vw",
    fontSize: "4vw",
    marginBottom: "2vw",
    padding: "1vw",
  };

  const labelStyle = {
    display: "block",
    fontSize: "4vw",
    marginBottom: "0.5vw",
    color: "#F9DF39",
  };

  return (
    <div
      style={{ display: "inline-block", fontSize: "5vw", paddingLeft: "10vw" }}
    >
      <title>ACTORS | Spies Among Us</title>
      <h1 style={{ fontSize: "8vw", marginBottom: "2vw", color: "#F9DF39" }}>
        {subdomain === "seattle" ? "SEATTLE" : "LOS ANGELES"}
      </h1>
      <h1>Actors</h1>

      <form onSubmit={handleSubmit} style={{ marginBottom: "6vw" }}>
        <h2>{editingId ? "Edit Actor" : "Create Actor"}</h2>
        <label style={labelStyle}>First Name</label>
        <input
          style={fieldStyle}
          value={form.firstName}
          onChange={(e) => setForm({ ...form, firstName: e.target.value })}
          required
        />
        <label style={labelStyle}>Last Name</label>
        <input
          style={fieldStyle}
          value={form.lastName}
          onChange={(e) => setForm({ ...form, lastName: e.target.value })}
        />
        <label style={labelStyle}>
          Headshot URL
          {editingId &&
            usesLocalAssets(actors.find((a) => a.id === editingId)) &&
            " (optional — local asset in use)"}
        </label>
        <input
          style={fieldStyle}
          value={form.headshotUrl}
          onChange={(e) => setForm({ ...form, headshotUrl: e.target.value })}
          placeholder="https://..."
        />
        <label style={labelStyle}>
          Polaroid URL
          {editingId &&
            usesLocalAssets(actors.find((a) => a.id === editingId)) &&
            " (optional — local asset in use)"}
        </label>
        <input
          style={fieldStyle}
          value={form.polaroidUrl}
          onChange={(e) => setForm({ ...form, polaroidUrl: e.target.value })}
          placeholder="https://..."
        />
        <label style={labelStyle}>Bio</label>
        <textarea
          style={{ ...fieldStyle, minHeight: "30vw" }}
          value={form.bio}
          onChange={(e) => setForm({ ...form, bio: e.target.value })}
        />
        {formError && (
          <div style={{ color: "#ff3700", marginBottom: "2vw" }}>{formError}</div>
        )}
        <button type="submit" className="settingsPageButton" disabled={saving}>
          {saving ? "Saving..." : editingId ? "Update Actor" : "Create Actor"}
        </button>
        {editingId && (
          <button
            type="button"
            className="settingsPageButton"
            onClick={resetForm}
            style={{ marginLeft: "2vw" }}
          >
            Cancel
          </button>
        )}
      </form>

      <h2>Existing Actors</h2>
      {actors.length === 0 && <p>No actors yet.</p>}
      {actors.map((actor) => {
        const headshot = resolveActorImage(actor, "headshot");
        const polaroid = resolveActorImage(actor, "polaroid");
        return (
          <div
            key={actor.id}
            style={{
              marginBottom: "4vw",
              paddingBottom: "4vw",
              borderBottom: "2px solid #ff3700",
            }}
          >
            <div style={{ fontWeight: "bold", marginBottom: "1vw" }}>
              {actorDisplayName(actor)}
              <span style={{ fontSize: "3vw", color: "#888", marginLeft: "2vw" }}>
                key: {actor.key}
              </span>
            </div>
            <div style={{ display: "flex", gap: "2vw", marginBottom: "2vw" }}>
              {headshot && (
                <img
                  src={headshot}
                  alt={`${actorDisplayName(actor)} headshot`}
                  onClick={() =>
                    setPreviewImage({
                      src: headshot,
                      alt: `${actorDisplayName(actor)} headshot`,
                    })
                  }
                  style={{
                    width: "20vw",
                    height: "auto",
                    objectFit: "cover",
                    cursor: "pointer",
                  }}
                />
              )}
              {polaroid && (
                <img
                  src={polaroid}
                  alt={`${actorDisplayName(actor)} polaroid`}
                  onClick={() =>
                    setPreviewImage({
                      src: polaroid,
                      alt: `${actorDisplayName(actor)} polaroid`,
                    })
                  }
                  style={{
                    width: "20vw",
                    height: "auto",
                    objectFit: "cover",
                    cursor: "pointer",
                  }}
                />
              )}
            </div>
            <div
              style={{ fontSize: "3vw", marginBottom: "2vw", maxWidth: "80vw" }}
              dangerouslySetInnerHTML={{
                __html: actor.bio || "<i>No bio</i>",
              }}
            />
            <button
              type="button"
              className="settingsPageButton"
              onClick={() => startEdit(actor)}
              style={{ marginRight: "2vw" }}
            >
              Edit
            </button>
            <button
              type="button"
              className="settingsPageButton"
              onClick={() => handleDelete(actor)}
            >
              Delete
            </button>
          </div>
        );
      })}

      <AdminNavigation currentPage="actors" />

      {previewImage && (
        <div
          onClick={closePreview}
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            width: "100%",
            height: "100%",
            backgroundColor: "rgba(0, 0, 0, 0.5)",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            zIndex: 1000,
            overflow: "auto",
          }}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={{
              background: "#21174C",
              width: "100%",
              height: "100%",
              overflowY: "auto",
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
            }}
          >
            <button
              type="button"
              onClick={closePreview}
              style={{
                position: "absolute",
                top: "1vw",
                right: "1vw",
                background: "transparent",
                border: "none",
                fontSize: "15vw",
                cursor: "pointer",
                color: "white",
                zIndex: 1,
              }}
            >
              &times;
            </button>
            <img
              src={previewImage.src}
              alt={previewImage.alt}
              style={{
                maxWidth: "90vw",
                maxHeight: "85vh",
                width: "auto",
                height: "auto",
                objectFit: "contain",
                marginTop: "12vw",
              }}
            />
          </div>
        </div>
      )}
    </div>
  );
}

export default ActorsAdmin;
